"use client"

import type { Page } from "content-collections"
import { type Locale, useLocale } from "next-intl"
import { useRouter } from "nextjs-toploader/app"
import type { HTMLAttributes } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { config } from "@/config"
import { cn } from "@/utils/cn"

interface LanguageSelectProps extends HTMLAttributes<HTMLButtonElement> {
  page: Page
}
export function LanguageSelect({ page, className, ...rest }: LanguageSelectProps) {
  const locale = useLocale()
  const router = useRouter()

  const onChange = (newLocale: Locale) => {
    if (locale === newLocale) return

    // Find alternate page in the requested locale (fallback to default locale)
    const languages = page.alternates.languages
    const alternateUrl = languages[newLocale] || languages[locale]
    if (!alternateUrl) return

    // Redirect to alternate page (without trailing slash)
    let alternatePath = new URL(alternateUrl).pathname
    alternatePath = alternatePath.replace(/\/$/, "")

    router.push(alternatePath, { scroll: true })
  }

  return (
    <Select onValueChange={onChange} defaultValue={locale} value={locale}>
      <SelectTrigger
        className={cn(
          "group !h-auto w-auto select-none gap-1.5 rounded-md border-none bg-foreground/5 px-3 py-1.5 font-medium hocus:text-foreground text-foreground/70 text-xs capitalize ring-offset-secondary transition-all hover:bg-foreground/10",
          "[&_svg]:text-foreground/60 [&_svg]:opacity-100 [&_svg]:transition-colors hover:[&_svg]:text-foreground",
          className,
        )}
        {...rest}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {config.i18n.locales.map((l) => (
          <SelectItem key={l} value={l} className={cn("capitalize")}>
            {new Intl.DisplayNames(locale, { type: "language" }).of(l)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
